import { type TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

export type TraineeTargetCombinator = "AND" | "OR";

/**
 * Declarative audience spec, resolved to concrete trainees by `resolveTargetedTrainees`.
 * Mirrors the group-message targeting (product / tag has·hasn't / status) and extends it with
 * a combinator (AND/OR), manual additions/removals, and reusable saved segments.
 */
export type TraineeTargetSpec = {
  /** How the positive filters (products, includeTags) combine. `statuses` + `excludeTags` are always AND. */
  combinator: TraineeTargetCombinator;
  /** Trainee owns an ACTIVE product_client row for any of these productIds. */
  productIds: string[];
  /** Trainee has at least one of these tags. */
  includeTags: string[];
  /** Trainee has none of these tags (hard exclude, always applied). */
  excludeTags: string[];
  /** Trainee.status in this set (empty = any status). */
  statuses: TraineeStatus[];
  /** Trainees added by hand — unioned onto the filter result. */
  manualIds: string[];
  /** Trainees explicitly removed from the resolved set. */
  excludedIds: string[];
};

export const emptyTraineeTargetSpec: TraineeTargetSpec = {
  combinator: "OR",
  productIds: [],
  includeTags: [],
  excludeTags: [],
  statuses: [],
  manualIds: [],
  excludedIds: [],
};

export type TargetedTrainee = {
  id: string;
  userId: string | null;
  firstName: string;
  lastName: string | null;
  email: string | null;
  tags: string[];
  status: TraineeStatus;
};

/** A reusable named audience (persisted in `trainee_segment`). */
export type TraineeSegment = {
  id: string;
  name: string;
  spec: TraineeTargetSpec;
};

export const isFilterActive = (spec: TraineeTargetSpec): boolean =>
  spec.productIds.length > 0 ||
  spec.includeTags.length > 0 ||
  spec.excludeTags.length > 0 ||
  spec.statuses.length > 0;
